import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Divider, Grid, InputLabel, Typography } from '@mui/material'
import { useHistory } from 'react-router-dom'

import { toast } from 'react-toastify'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { ClientRiskScoreEnum } from '@sparling/financial-common'

import { routes } from '../../../constants/routes'
import { USER_TYPES } from '../../../constants/userTypes'
import { TransparentButton } from '../../../components/Buttons/TransparentButton'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { InputField } from '../../../components/InputField'
import { InputSelect } from '../../../components/InputSelect'
import { DataPicker } from '../../../components/DatePicker'
import { InputSwitch } from '../../../components/Buttons/InputSwitch'
import CountrySelect from '../../../components/CountrySelect'
import { useRest } from '../../../components/Context'
import useDebounce from '../../../hooks/useDebounce'
import { Colors } from '../../../theme'
import { InputPhone } from '../../../components/InputPhone'
import { SelectWithSearch } from '../../../components/SelectWithSearch'
import {
  clientTypesForAssignToUserLoading,
  entitiesForAssignToUserLoading,
  getClientTypesForAssignToUserEnum,
  getEntitiesForAssignToUserEnum,
  fiatsEnumSelector,
} from '../../../store/selectors'
import { getBusinessEntitiesForAssignToUserRequest, getClientTypesForAssignToUserRequest } from '../../../store/actions'
import { createEnum } from '../../../utils/createEnum'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  formContent: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    margin: '32px 32px 32px 0',
    padding: 32,
    background: 'rgba(216, 216, 216, 0.05)',
  },
  inputWrap: {
    justifyContent: 'space-between',
    alignItems: 'stretch',
    width: 632,
    flexWrap: 'wrap',
  },
  switchWrap: {
    width: 300,
    paddingLeft: 0,
    color: 'rgba(255,255,255,0.72)',
    '& label': {
      justifyContent: 'space-between',
      paddingTop: 10,
    },
  },
  formSwitch: {
    marginLeft: 0,
  },
  horizontalLine: {
    backgroundColor: '#767a7e',
    margin: '32px 32px 32px 0',
    width: '100%',
  },
  birthdayWrap: {
    maxHeight: 63,
  },
  birthdayInput: {
    fontSize: '14px',
    // marginTop: 3,
    color: '#FFFFFF',
    opacity: '60%',
  },
  uploadWrap: {
    marginTop: 23,
  },
  switch: {
    display: 'flex',
    width: 300,
    height: 40,
    marginTop: 23,
    marginBottom: 23,
    '&>div': {
      alignSelf: 'center',
      margin: '0 auto',
    },
  },
  userPhoneError: {
    marginTop: -20,
    width: 300,
    color: Colors.error,
  },
}))

const GridRow: FC = ({ children }) => {
  return (
    <Grid item container spacing={4}>
      {children}
    </Grid>
  )
}

// const resolver = classValidatorResolver(CreateUserRequestDto)

type Inputs = {
  firstName: string
  lastName: string
  email: string
  phone: string
  country: string
  city: string
  companyName: string | null
  birthday: string
  role: string
  currency: string
  documentType?: string
  twoFaEnabled: boolean
  businessEntity?: string | null
  clientType: number
  riskScore: ClientRiskScoreEnum
}

export const CreateUser: FC = () => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    // resolver,
  })
  const classes = useStyles()
  const { rxios } = useRest()
  const history = useHistory()
  const { handleSubmit, watch, unregister, register, formState, getValues, control } = formMethods
  // eslint-disable-next-line
  const [companySelect, setCompanySelect] = useState<any>([])
  const [isWatcher, setWatcher] = useState<boolean>(true)
  const [userPhoneError, setUserPhoneError] = useState<string | boolean>(false)
  const [userPhone, setUserPhone] = useState<string | undefined>()
  const debouncedValue = useDebounce<string | undefined>(userPhone, 200)

  const dispatch = useDispatch()
  const loadingEntitiesForAssignToUser = useSelector(entitiesForAssignToUserLoading)
  const entitiesForAssignToUser = useSelector(getEntitiesForAssignToUserEnum)
  const loadingClientTypesForAssignToUser = useSelector(clientTypesForAssignToUserLoading)
  const clientTypesForAssignToUser = useSelector(getClientTypesForAssignToUserEnum)
  const fiatsEnum = useSelector(fiatsEnumSelector)

  const getBusinessEntities = useCallback(
    (search: string) => {
      dispatch(getBusinessEntitiesForAssignToUserRequest({ search }))
    },
    [dispatch],
  )

  const getClientTypes = useCallback(
    (search: string) => {
      dispatch(getClientTypesForAssignToUserRequest({ search }))
    },
    [dispatch],
  )

  const phoneVerification = useCallback(
    (phone: string) => {
      rxios
        .post('user/unique-phone', {
          phone,
        })
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            if (next.success) {
              setUserPhoneError(false)
            } else {
              setUserPhoneError(next.message)
            }
          },
          (error) => {
            toast.error(error.response.data.message)
          },
        )
      // eslint-disable-next-line
    },
    [rxios],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'role') {
        if (value[name] === 'sole_trader') {
          setWatcher(false)
          unregister('companyName')
        } else {
          setWatcher(true)
          register('companyName')
        }
      }
      if (name === 'phone') setUserPhone(value[name])
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line
  }, [watch])

  useEffect(() => {
    if (formState.errors.phone) {
      setUserPhoneError(false)
    }
    if (userPhone) phoneVerification(userPhone)
    // eslint-disable-next-line
  }, [debouncedValue])

  const onSubmit = useCallback((data: Inputs) => {
    rxios.post('user', data).subscribe(
      // eslint-disable-next-line
      (next: any) => {},
      // eslint-disable-next-line
      (error: any) => {
        toast.error(error.response?.data?.message || error.message)
      },
      () => {
        toast.success(`User ${data.firstName} ${data.lastName} was created`)
        history.push(routes.USERS)
      },
    )
    // eslint-disable-next-line
  }, [])

  // eslint-disable-next-line
  const formSubmit = () => {
    if (!isWatcher && formState.errors.companyName) {
      const params = {
        ...getValues(),
        companyName: null,
      }
      onSubmit(params)
    } else {
      handleSubmit(onSubmit)()
    }
  }

  const loadCompanyList = useCallback(async () => {
    rxios.get(`company-names`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const company = []

        // eslint-disable-next-line no-restricted-syntax
        for (const name of next) {
          company.push({
            value: name,
            label: name,
          })
        }
        setCompanySelect(company)
      },
      (error) => {
        toast.error(error)
      },
    )
    // eslint-disable-next-line
  }, [rxios])

  useEffect(() => {
    ;(async () => {
      await loadCompanyList()
    })()
    // eslint-disable-next-line
  }, [])

  const selectedEntityId = useMemo(() => getValues().businessEntity ?? null, [getValues])
  const selectedClientTypeId = useMemo(() => getValues().clientType ?? null, [getValues])

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(formSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Create User
          </Typography>
          <Grid container item className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={history.goBack} text="Cancel" />
            <GreenButton type="submit" width={120} text="Save" />
          </Grid>
        </Grid>
        <Grid item container className={classes.formContent}>
          <Grid item container className={classes.inputWrap}>
            <InputField inputName={'firstName'} labelName={'First Name'} inputType={'text'} isRequired />
            <InputField inputName={'lastName'} labelName={'Last Name'} inputType={'text'} isRequired />
            <InputField inputName={'email'} labelName={'Email'} inputType={'text'} isCompanyEmail isRequired />
            <Typography component={'div'} className={classes.birthdayWrap}>
              <InputLabel className={classes.birthdayInput}>Date of Birth</InputLabel>
              <DataPicker width={300} margin={0} mt={3} dataPickerName={'birthday'} required={true} />
            </Typography>
            <CountrySelect
              inputName={'country'}
              labelName={'Country'}
              placeholder={'Please Select'}
              width={300}
              mb={25}
            />
            <InputField inputName={'city'} labelName={'City'} inputType={'text'} isRequired />
            <InputPhone phoneError={userPhoneError} />
            <InputSelect
              inputName={'currency'}
              labelName={'Currency'}
              width={300}
              placeholder={'Please Select'}
              mb={25}
              enums={fiatsEnum}
            />
            <Grid item className={classes.switch}>
              <InputSwitch label={'Two-Factor Authentication:'} />
            </Grid>
          </Grid>

          <Divider className={classes.horizontalLine} variant={'fullWidth'} />
          <Grid item container className={classes.inputWrap}>
            <InputSelect
              inputName={'role'}
              labelName={'User Type'}
              width={300}
              placeholder={'Please Select'}
              mb={25}
              enums={USER_TYPES}
            />
            {isWatcher && (
              <InputSelect
                inputName={'companyName'}
                labelName={'Company'}
                width={300}
                placeholder={'Please Select'}
                mb={25}
                enums={companySelect}
              />
            )}
            {/* <GridRow> */}
            {/*  <Grid item> */}
            {/*    <InputSelect */}
            {/*      inputName={'documentType'} */}
            {/*      labelName={'Document Type'} */}
            {/*      placeholder={'Please Select'} */}
            {/*      width={300} */}
            {/*      mb={25} */}
            {/*    /> */}
            {/*  </Grid> */}
            {/*  <Grid item className={classes.uploadWrap}> */}
            {/*    <UploadButton postfix={icons.CLOUD} text={'Upload Document'} mb={25} width={300} /> */}
            {/*  </Grid> */}
            {/* </GridRow> */}
            <GridRow>
              <Grid item>
                <SelectWithSearch
                  inputName={'clientType'}
                  labelName={'Client Type'}
                  placeholder={'Please Select'}
                  noOptionsText={'Client types not found'}
                  control={control}
                  selectedId={selectedClientTypeId}
                  loading={loadingClientTypesForAssignToUser}
                  options={clientTypesForAssignToUser}
                  onSearch={getClientTypes}
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item>
                <InputSelect
                  inputName={'riskScore'}
                  labelName={'Client Risk Score'}
                  width={300}
                  placeholder={'Please Select'}
                  mb={25}
                  enums={createEnum(ClientRiskScoreEnum)}
                />
              </Grid>
            </GridRow>

            <GridRow>
              <Grid item>
                <SelectWithSearch
                  inputName={'businessEntity'}
                  labelName={'Business Entity'}
                  placeholder={'Please Select'}
                  noOptionsText={'Entities not found'}
                  control={control}
                  selectedId={selectedEntityId}
                  loading={loadingEntitiesForAssignToUser}
                  options={entitiesForAssignToUser}
                  onSearch={getBusinessEntities}
                />
              </Grid>
            </GridRow>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
